import { requestWithToken, requestURL } from '../utilities/request';
import * as _ from 'lodash';
import moment from 'moment';
import SessionStorageService from './utilities/SessionStorageService';
import ReportNames from '../utilities/reportNames';
import {
  FILE_CODE,
  STATUS_PRINT_ERROR_RESPONSE_CODE,
} from '../utilities/constants';
import { messageUtility } from '../utilities/messageUtility';

const path = '/api/private/print_outs';
const PATH_API_PRIVATE = 'api/private';

/**
 * Get report by report API
 * @param pathApi   {String}
 * @param params    {Object}
 * @param hasFilter {Number} true:not display error / false:display error
 */
const getReport = (pathApi, params, hasFilter) => {
  let type = params.file_code === FILE_CODE.PDF ? 'json' : 'blob';
  return requestWithToken({
    method: 'GET',
    url: PATH_API_PRIVATE.concat(pathApi),
    params: {
      ...params,
    },
    responseType: type,
    action: 'print',
    hasFilter,
  });
};

/**
 * Print report by report name
 * @param reportName {String}
 * @param params     {Object}
 * @param hasFilter  {Number} true:not display error / false:display error
 * @return a Promise object
 */
const printReportByReportName = (reportName, params, hasFilter) => {
  const targetReport = _.find(Reports, r => r.name === reportName);
  const args = _.zipObject(
    targetReport.args,
    _.map(targetReport.args, key => params[key])
  );

  if (!targetReport) {
    return Promise.reject(`Report not found.`);
  }
  return getReport(targetReport.path, args, hasFilter);
};

/**
 * Get report by report API with specific action
 * ※一覧系の画面から帳票出力処理を実施する場合に個別のエラーハンドリングを実施するための関数です。
 * @param pathApi    {String}
 * @param params     {Object}
 * @param hasFilter  {Number} true:not display error / false:display error
 * @param actionType {String} if you control error handling, put 'printList' into this param
 */
const getReportWithAction = (pathApi, params, hasFilter, actionType) => {
  let type = params.file_code === FILE_CODE.PDF ? 'json' : 'blob';
  return requestWithToken({
    method: 'GET',
    url: PATH_API_PRIVATE.concat(pathApi),
    params: {
      ...params,
    },
    responseType: type,
    action: actionType,
    hasFilter,
  });
};

/**
 * Print report by report name from Screen List (use action 'printList')
 * ※一覧系の画面から帳票出力処理を実施する場合に個別のエラーハンドリングを実施するための関数です。
 * @param reportName {String}
 * @param params     {Object}
 * @param hasFilter  {Number} true:not display error / false:display error
 * @return a Promise object
 */
const printReportByReportNameForList = (reportName, params, hasFilter) => {
  const targetReport = _.find(Reports, r => r.name === reportName);
  const args = _.zipObject(
    targetReport.args,
    _.map(targetReport.args, key => params[key])
  );

  if (!targetReport) {
    return Promise.reject(`Report not found.`);
  }
  return getReportWithAction(targetReport.path, args, hasFilter, 'printList');
};

/**
 * Handle response error message from Screen List (use action 'printList')
 * @param {object} response json object
 * @param {function} callBackErrorMessage a callback to return errorMessage for display ErrorDialog later
 * @returns true if any error occurs, false otherwise
 */
const handleShowErrorDialogForList = (response, callBackErrorMessage) => {
  if (!response) {
    return true;
  }

  // check if 'response.result' is defined and 'response.result' = false
  // if 'response.result' is undefined, 'response' is the result of conversion from Excel file blob, then in this case, we have to skip checking it.
  if (typeof response.result === 'boolean' && !response.result) {
    // Always display default message [E-001-414] when error status is not one of error message from STATUS_PRINT_ERROR_RESPONSE_CODE
    // Do not use error message from V2(Rails)
    const errorMessage = getErrorMessageForList(response.status);

    if (errorMessage) {
      callBackErrorMessage(errorMessage);
      return true;
    }
  }

  return false;
};

/**
 * Get corresponding error message from Screen List (use action 'printList')
 * @param {number} statusCode 413, 422, 507,...
 * @param {string} message error message from API (Rails)
 * @returns corresponding error message
 */
const getErrorMessageForList = (
  statusCode,
  message = messageUtility.getMessage('E-001-414')
) => {
  switch (statusCode) {
    case STATUS_PRINT_ERROR_RESPONSE_CODE.docurainPaymentThresHold:
      return messageUtility.getMessage('E-001-410');
    case STATUS_PRINT_ERROR_RESPONSE_CODE.noData:
      return messageUtility.getMessage('E-001-411');
    case STATUS_PRINT_ERROR_RESPONSE_CODE.reachMaxPage:
      return messageUtility.getMessage('E-001-412');
    default:
      return message;
  }
};

/**
 * Make report file name
 * @param reportName {String}
 * @param fileCode {Interger}
 * @param targetDate {String}
 */
const makeReportFileName = (reportName, fileCode, targetDate) => {
  const targetReport = _.find(Reports, r => r.name === reportName);
  let date = moment(targetDate).format('YYYYMMDD');
  const prefix = fileCode === 1 ? 'xlsx' : 'pdf';

  if (
    fileCode === FILE_CODE.EXCEL &&
    targetReport.hasOwnProperty('excelFileName')
  ) {
    if (
      targetReport.hasOwnProperty('excelNoDate') &&
      targetReport.excelNoDate === 1
    ) {
      return `${targetReport.excelFileName}.${prefix}`;
    }
    if (targetReport.hasOwnProperty('formatTime')) {
      date = `${date}_${moment().format(targetReport.formatTime)}`;
    }
    if (
      targetReport.hasOwnProperty('prefixExcelDate') &&
      targetReport.prefixExcelDate
    ) {
      return `${date}_${targetReport.excelFileName}.${prefix}`;
    }
    if (
      targetReport.hasOwnProperty('prefixExcelDateNoUnderscore') &&
      targetReport.prefixExcelDateNoUnderscore
    ) {
      return `${date}${targetReport.excelFileName}.${prefix}`;
    }
    return `${targetReport.excelFileName}_${date}.${prefix}`;
  }

  if (
    fileCode === FILE_CODE.PDF &&
    targetReport.hasOwnProperty('pdfFileName')
  ) {
    if (
      targetReport.hasOwnProperty('pdfNoDate') &&
      targetReport.pdfNoDate === 1
    ) {
      return `${targetReport.pdfFileName}.${prefix}`;
    }
    if (
      targetReport.hasOwnProperty('prefixPdfDate') &&
      targetReport.prefixPdfDate
    ) {
      return `${date}_${targetReport.pdfFileName}.${prefix}`;
    }
    return `${targetReport.pdfFileName}_${date}.${prefix}`;
  }

  return `${_.snakeCase(reportName)}.${prefix}`;
};

const Reports = [
  {
    name: ReportNames.WorkAndDiary,
    excelFileName: '作業間連絡及び調整実施記録（安全衛生日誌有）',
    pdfFileName: 'WorkAndDiary',
    path: '/reports/work_and_diary',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'report_code',
      'schedule_flag',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
    ], // 引数に合うようにparamsのkeyをArrayで指定する
  },
  {
    name: ReportNames.Adjust,
    excelFileName: '作業間連絡及び調整実施記録（安全衛生日誌無）',
    pdfFileName: 'Work',
    path: '/reports/adjust',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'schedule_flag',
      'private_partner_company_flag',
      'tantou',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
    ],
  },
  {
    name: ReportNames.Diary,
    excelFileName: '安全衛生日誌',
    pdfFileName: 'Diary',
    path: '/reports/diary',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'report_code',
      'schedule_flag',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
    ],
  },
  {
    name: ReportNames.AllKYWork,
    excelFileName: '作業安全指示書及び現地ＫＹ記録表',
    pdfFileName: 'All_Ky_Work',
    path: '/reports/all_ky_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'resize_image_flag',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.AllAdjustWork,
    excelFileName: '作業安全指示書（予定）',
    pdfFileName: 'All_Adjust_Work',
    path: '/reports/all_adjust_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.AllResultWork,
    excelFileName: '作業安全指示書（実施）',
    pdfFileName: 'All_Result',
    path: '/reports/all_result_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.AllKY,
    excelFileName: '現地ＫＹ記録表',
    pdfFileName: 'Bld_Mtg_All_Ky',
    path: '/reports/all_ky',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.AllCraneUse,
    excelFileName: '揚重作業計画・作業指示書',
    pdfFileName: 'All_Crane_Use',
    path: '/reports/all_crane_use',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'approval_flag',
      'spot_flag',
      'crane_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.GatePassageDaily,
    excelFileName: '車輌一覧（日次）',
    pdfFileName: 'Gate_Passage_Daily',
    path: '/reports/gate_passage_daily',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'approval_flag',
      'crane_use',
      'spot_flag',
      'gate_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'status_code',
      'report_code',
    ],
  },
  {
    name: ReportNames.GatePassageWeekly,
    excelFileName: '車輌一覧（週間）',
    pdfFileName: 'Gate_Passage_Weekly',
    path: '/reports/gate_passage_weekly',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'approval_flag',
      'crane_use',
      'spot_flag',
      'gate_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'status_code',
      'report_code',
    ],
  },
  {
    name: ReportNames.CraneUseDaily,
    excelFileName: '揚重一覧（日次）',
    pdfFileName: 'Crane_Use_Daily',
    path: '/reports/crane_use_daily',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'crane_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.AllMachineryInstruction,
    excelFileName: '機材　計画・作業指示書',
    pdfFileName: 'Machinery_Instruction',
    path: '/reports/all_machinery_instruction',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'approval_flag',
      'spot_flag',
      'machinery_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.MachineryUse,
    excelFileName: '機材一覧',
    pdfFileName: 'Machinery_Use',
    path: '/reports/machinery_use',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'report_code',
      'machinery_type',
    ],
  },
  {
    name: ReportNames.CraneUseWeekly,
    excelFileName: '揚重一覧（週間）',
    pdfFileName: 'Crane_Use_Weekly',
    path: '/reports/crane_use_weekly',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'approval_flag',
      'spot_flag',
      'crane_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.HotWorkManager,
    excelFileName: '火気使用届',
    pdfFileName: 'All_Hot_Work_Manager',
    path: '/reports/hot_work_manager',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.HotWork,
    excelFileName: '火気使用届・許可・終了確認書（一覧）',
    pdfFileName: 'Hot_Work',
    path: '/reports/hot_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.AllOxygenLackPartnerCompany,
    excelFileName: '酸欠帳票',
    pdfFileName: 'All_Oxygen_Lack_Partner_Company',
    path: '/reports/all_oxygen_lack_partner_company',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.EarlytimeWork,
    excelFileName: '早出一覧',
    pdfFileName: 'EarlyTime_Work',
    path: '/reports/earlytime_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.OvertimeWork,
    excelFileName: '残業一覧',
    pdfFileName: 'Overtime_Work',
    path: '/reports/overtime_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.EarlytimeNotify,
    excelFileName: '早出届',
    pdfFileName: 'Earlytime_Notify',
    path: '/reports/earlytime_notify',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.OvertimeNotify,
    excelFileName: '残業届',
    pdfFileName: 'Overtime_Notify',
    path: '/reports/overtime_notify',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.AllFixedWorkDetailedStatementPayment,
    excelFileName: '出面及支払内訳書（請負）',
    pdfFileName: 'All_Fixed_Work_Detailed_Statement_Payment',
    path: '/reports/all_fixed_work_detailed_statement_payment',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'is_partnerLv1_print',
      'field_tree_id',
      'foreman_user_id',
      'is_foreman_print',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.AllUnfixedWorkDetailedStatementPayment,
    excelFileName: '出面及支払内訳書(常傭)',
    pdfFileName: 'All_Unfixed_Work_Detailed_Statement_Payment',
    path: '/reports/all_unfixed_work_detailed_statement_payment',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'is_partnerLv1_print',
      'field_tree_id',
      'foreman_user_id',
      'is_foreman_print',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.AllWorkDetailedStatementPayment,
    excelFileName: '出面及支払内訳書（請負＋常傭）',
    pdfFileName: 'All_Work_Detailed_Statement_Payment',
    path: '/reports/all_work_detailed_statement_payment',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'is_partnerLv1_print',
      'field_tree_id',
      'foreman_user_id',
      'is_foreman_print',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.FixedWorkTotal,
    excelFileName: '作業人員集計（請負）',
    pdfFileName: 'Fixed_Work_Total',
    path: '/reports/fixed_work_total',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.UnfixedWorkTotal,
    excelFileName: '作業人員集計（常傭）',
    pdfFileName: 'Unfixed_Work_Total',
    path: '/reports/unfixed_work_total',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.WorkTotal,
    excelFileName: '作業人員集計（請負・常傭計）',
    pdfFileName: 'Work_Total',
    path: '/reports/work_total',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.GatePassageMonthly,
    excelFileName: '車輌搬入出予定月間集計表',
    pdfFileName: 'Gate_Passage_Monthly',
    path: '/reports/gate_passage_monthly',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'approval_flag',
      'crane_use',
      'spot_flag',
      'gate_id',
      'time_zone',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'status_code',
      'report_code',
    ],
  },
  {
    name: ReportNames.CraneUseMonthly,
    excelFileName: '揚重予定月間集計表',
    pdfFileName: 'Crane_Use_Monthly',
    path: '/reports/crane_use_monthly',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'time_zone',
      'approval_flag',
      'spot_flag',
      'crane_id',
      'field_tree_id',
      'manager_user_id',
      'field_group_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.MonthWork,
    excelFileName: '月間作業一覧表',
    pdfFileName: 'Month_Work',
    path: '/reports/month_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'time_zone',
      'report_code',
    ],
  },
  {
    name: ReportNames.PrintSafetyDiary,
    excelFileName: '安全指摘事項',
    pdfFileName: 'Deignation_Safety',
    path: '/reports/print_safety_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'type_print',
      'dateType',
      'dateStart',
      'dateEnd',
      'type',
      'statuses',
      'urgencies',
      'notifications_manager_ids',
      'notification_field_tree_ids',
      'notifications_group_ids',
      'notifications_manager_flag',
      'notifications_company_flag',
      'notifications_group_flag',
      'resize_image_flag',
      'self_notify_flag',
      'journal_types',
    ],
  },
  {
    name: ReportNames.PrintSafetyDiaryList,
    excelFileName: '安全指摘事項（一覧）',
    pdfFileName: 'Deignation_Safety_List',
    path: '/reports/print_safety_diary_list',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'dateType',
      'dateStart',
      'dateEnd',
      'type',
      'statuses',
      'urgencies',
      'notifications_manager_ids',
      'notification_field_tree_ids',
      'notifications_group_ids',
      'notifications_manager_flag',
      'notifications_company_flag',
      'notifications_group_flag',
      'self_notify_flag',
      'journal_types',
    ],
  },
  {
    name: ReportNames.PrintoutPrev,
    path: '/reports/printout_prev',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'option4_display_flag',
      'option4_print_flag',
      'option5_display_flag',
      'option5_print_flag',
      'option6_display_flag',
      'option6_print_flag',
      'option4_title',
      'option5_title',
      'option6_title',
      'option4_print_width',
      'option5_print_width',
      'option6_print_width',
    ],
  },
  {
    name: ReportNames.PrintoutPrev2,
    path: '/reports/printout_prev2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'option4_display_flag',
      'option4_print_flag3',
      'option5_display_flag',
      'option5_print_flag3',
      'option6_display_flag',
      'option6_print_flag3',
      'option4_title',
      'option5_title',
      'option6_title',
      'option4_print_width3',
      'option5_print_width3',
      'option6_print_width3',
    ],
  },
  {
    name: ReportNames.AllNishimatsuKy,
    excelFileName: '作業指示・安全指示書及び現地ＫＹ記録表',
    path: '/reports/all_nishimatsu_ky',
    args: [
      'field_id',
      'work_hour_zone',
      'target_date',
      'file_code',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.NishimatsuKNisshi,
    excelFileName: '作業打合せ指示の記録',
    pdfFileName: 'Nishimatsu_Work_And_Diary',
    path: '/reports/nishimatsu_k_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'c_item_id',
      'a_item_ids',
      'c_item_ids',
      'report_code',
    ],
  },
  {
    name: ReportNames.CstmGoyoField,
    excelFileName: '作業打合せ・管理日誌',
    path: '/reports/cstm_goyo_field',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.CstmGoyo,
    excelFileName: '作業打合せ・管理日誌',
    pdfFileName: 'cstm_goyo',
    path: '/reports/cstm_goyo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.KyGoyo,
    excelFileName: '「危険予知」並びに「予定外作業打合せ書」',
    path: '/reports/ky_goyo',
    args: ['file_code', 'report_code'],
  },
  {
    name: ReportNames.CraneGoyo,
    excelFileName: '移動式クレーン作業計画書',
    path: '/reports/crane_goyo',
    args: ['file_code', 'report_code'],
  },
  {
    name: ReportNames.HighplaceGoyo,
    excelFileName: '高所作業車作業計画書',
    path: '/reports/highplace_goyo',
    args: ['file_code', 'report_code'],
  },
  {
    name: ReportNames.VehicleGoyo,
    excelFileName: '車両系建設機械作業計画書',
    path: '/reports/vehicle_goyo',
    args: ['file_code', 'report_code'],
  },
  {
    name: ReportNames.FireworkGoyo,
    excelFileName: '火気使用届・火気作業終了報告書',
    path: '/reports/firework_goyo',
    args: ['file_code', 'report_code'],
  },
  {
    name: ReportNames.MatsuiSafety,
    excelFileName: '作業所安全衛生日誌・指示書',
    path: '/reports/matsui_safety',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.TaiseiSafety,
    excelFileName: '工事・安全衛生環境打合せ書',
    path: '/reports/taisei_safety',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuWorkCommon,
    excelFileName: '作業安全指示書（一般）',
    path: '/reports/seibu_work_common',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuWorkRail,
    excelFileName: '作業安全指示書（鉄道）',
    path: '/reports/seibu_work_rail',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuSafety,
    excelFileName: '工事・安全日誌',
    path: '/reports/seibu_safety',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.KSagyoyotei,
    excelFileName: '作業間連絡調整及び安全衛生指示書',
    path: '/reports/k_sagyoyotei',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'tantou',
      'is_approved',
      'dayworker_type',
      'private_partner_company_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.KNisshi,
    excelFileName: '安全衛生日誌',
    path: '/reports/k_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.KJisshi,
    excelFileName: '作業実施記録',
    path: '/reports/k_jisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'tantou',
      'is_approved',
      'dayworker_type',
      'private_partner_company_flag',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.CstmObayashiAllAdjustWork,
    excelFileName: '作業及び安全衛生打合日報（手配）',
    pdfFileName: 'all_adjust_work',
    pdfNoDate: 1,
    path: '/reports/cstm_obayashi_all_adjust_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.CstmObayashiAllResultWork,
    excelFileName: '作業及び安全衛生打合日報（実績）',
    pdfFileName: 'all_result_work',
    pdfNoDate: 1,
    path: '/reports/cstm_obayashi_all_result_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.CstmObayashiWorkSafety,
    excelFileName: '作業打合せおよび安全衛生環境指示書・安全衛生日誌',
    path: '/reports/cstm_obayashi_work_safety',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'private_partner_company_flag',
      'tantou',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.KKosuseiri,
    excelFileName: '工数整理表',
    path: '/reports/k_kosuseiri',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'time_zone',
      'field_tree_id',
      'is_partnerLv1_print',
      'approval_flag',
      'start_date',
      'foreman_user_id',
      'is_foreman_print',
      'report_code',
    ],
  },
  {
    name: ReportNames.Saigaitok,
    excelFileName: '災害統計用集計表',
    path: '/reports/saigaitok',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'start_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Nisshi,
    excelFileName: '工事・安全衛生日誌 兼 環境・品質日誌',
    prefixExcelDate: true,
    path: '/reports/nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Shiji,
    excelFileName: '安全作業指示書',
    pdfFileName: 'shiji',
    formatTime: 'HHmm',
    path: '/reports/shiji',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Ky,
    excelFileName: '危険予知活動表',
    pdfFileName: 'ky',
    formatTime: 'HHmm',
    path: '/reports/ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Anzenshiji,
    excelFileName: '作業安全指示書・日誌',
    path: '/reports/anzenshiji',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Shinchoku,
    excelFileName: '進捗状況報告書',
    path: '/reports/shinchoku',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.NishimatsuDNisshi,
    excelFileName: '【西松建設書式】作業打合せ指示の記録',
    path: '/reports/nishimatsu_d_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'c_item_id',
      'a_item_ids',
      'c_item_ids',
    ],
  },
  {
    name: ReportNames.Takuma,
    excelFileName: '【タクマ書式】安全衛生･品質・環境作業打合せ書',
    path: '/reports/takuma',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.TaiseiSafety2,
    excelFileName: '工事・安全衛生環境打合せ書（場所別）',
    path: '/reports/taisei_safety2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuWorkCommonPart2,
    excelFileName: '作業安全指示書（一般）',
    path: '/reports/seibu_work_common_part2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuWorkRailPart2,
    excelFileName: '作業安全指示書（鉄道）',
    path: '/reports/seibu_work_rail_part2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuSafety2,
    excelFileName: '工事・安全日誌',
    path: '/reports/seibu_safety2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.SeibuSafety3,
    excelFileName: '工事・安全日誌',
    path: '/reports/seibu_safety3',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Geppo,
    excelFileName: `${moment(SessionStorageService.getFromDate()).format(
      'M'
    )}月死傷災害月報`,
    path: '/reports/geppo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.OkumuraUchiawase,
    excelFileName: '作業内容打合せ書',
    path: '/reports/okumura_uchiawase',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.OkumuraNisshi,
    excelFileName: '安全衛生・品質・環境点検日誌',
    path: '/reports/okumura_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Ky1,
    excelFileName: '現地ＫＹ活動表',
    path: '/reports/ky1',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.TakenakadobokuNisshi,
    excelFileName: '作業間連絡及び調整実施記録',
    path: '/reports/takenakadoboku_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.TakenakadobokuKy,
    excelFileName: '作業安全指示書及び現地KY記録表',
    path: '/reports/takenakadoboku_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Tachiai,
    excelFileName: '期中確認の予定',
    path: '/reports/tachiai',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.ToutetsuNisshiJR,
    excelFileName: '作業打合せ・安全作業指示・工事・安全衛生日誌【JR用】',
    pdfFileName: 'toutetsu_nisshi_JR',
    pdfNoDate: 1,
    path: '/reports/toutetsu_nisshi_jr',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.ToutetsuKyJR,
    excelFileName: '指揮命令系統図及びＫＹ活動報告書【JR用】',
    pdfFileName: 'toutetsu_ky_JR',
    pdfNoDate: 1,
    path: '/reports/toutetsu_ky_jr',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.NakayamaNisshi,
    excelFileName: '安全作業指示書・安全日誌',
    pdfFileName: 'nakayama_nisshi',
    path: '/reports/nakayama_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.NakayamaKy,
    excelFileName: '現地ＫＹ活動表',
    pdfFileName: 'nakayama_ky',
    path: '/reports/nakayama_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.MitsuisumitomoNissi,
    excelFileName: '作業打合せ及び安全衛生日誌(場所欄有り)',
    pdfFileName: 'mitsuisumitomo_nissi_bashoari',
    path: '/reports/mitsuisumitomo_nissi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
    ],
  },
  {
    name: ReportNames.ZenitakaMeeting,
    excelFileName: '安全作業指示ミーティング日誌',
    path: '/reports/zenitaka_meeting',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.ZenitakaAnzen,
    excelFileName: '安全衛生日誌',
    path: '/reports/zenitaka_anzen',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.KumagaiUchiawase,
    excelFileName: '作業打合せ簿',
    path: '/reports/kumagai_uchiawase',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Uchiawase,
    excelFileName: '工事・安全衛生環境品質打合せ書',
    path: '/reports/uchiawase',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.UchiawaseshoNippo,
    excelFileName: '作業安全打合せ書・安全日誌_安全ミーティング日報',
    path: '/reports/uchiawasesho_nippo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Nippo,
    excelFileName: '【日本建設書式】安全工事日報',
    path: '/reports/nippo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.ShijiNisshi,
    excelFileName: '作業安全指示書・作業日誌',
    pdfFileName: 'shiji_nisshi',
    path: '/reports/shiji_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.ShijiKy,
    excelFileName: '現地ＫＹ記録書',
    pdfFileName: 'shiji_ky',
    path: '/reports/shiji_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'is_foreman_print',
      'a_item_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.KawaguchiNisshi,
    excelFileName: '作業打合せ及び安全衛生日誌',
    path: '/reports/kawaguchi_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.ObayashiAllAdjustWork,
    excelFileName: '作業及び安全衛生打合日報（手配）',
    pdfFileName: 'all_adjust_work',
    pdfNoDate: 1,
    path: '/reports/obayashi_all_adjust_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.ObayashiAllResultWork,
    excelFileName: '作業及び安全衛生打合日報（実績）',
    pdfFileName: 'all_result_work',
    pdfNoDate: 1,
    path: '/reports/obayashi_all_result_work',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'report_code',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
      'schedule_flag',
    ],
  },
  {
    name: ReportNames.WorkAndDiary2,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'work_and_diary2',
    path: '/reports/work_and_diary2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'schedule_flag',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
    ],
  },
  {
    name: ReportNames.HpsNisshiL,
    excelFileName: '【大型現場用】安全日誌',
    pdfFileName: 'hps_nisshi_L',
    path: '/reports/hps_nisshi_l',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.HpsNisshiS,
    excelFileName: '【小口現場用】安全日誌',
    pdfFileName: 'hps_nisshi_S',
    path: '/reports/hps_nisshi_s',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.HpsKyL,
    excelFileName: '【大型現場用】ＲＫＹ',
    pdfFileName: 'hps_ky_L',
    path: '/reports/hps_ky_l',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'field_tree_id',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.HpsKyS,
    excelFileName: '【小口現場用】ＲＫＹ',
    pdfFileName: 'hps_ky_S',
    path: '/reports/hps_ky_s',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'field_tree_id',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.NkkTowerCraneMeetingForm,
    excelFileName: 'タワークレーン作業計画・打合せ書',
    pdfFileName: 'nkk_tower_crane_meeting_form',
    path: '/reports/nkk_tower_crane_meeting_form',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.NkkVehicleMachineryMeetingForm,
    excelFileName: '車両系機械作業計画・打合せ書',
    pdfFileName: 'nkk_vehicle_machinery_meeting_form',
    path: '/reports/nkk_vehicle_machinery_meeting_form',
    args: [
      'field_id',
      'target_date',
      'work_hour_zone',
      'file_code',
      'report_code',
    ],
  },
  {
    name: ReportNames.NkkTransportableCraneMeetingForm,
    excelFileName: '移動式クレーン作業計画・打合せ書',
    pdfFileName: 'nkk_transportable_crane_meeting_form',
    path: '/reports/nkk_transportable_crane_meeting_form',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.KgtNisshi,
    excelFileName: '安全日誌',
    pdfFileName: 'kgt_nisshi',
    path: '/reports/kgt_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.TakenakadobokuNisshiBasho,
    excelFileName: '作業間連絡及び調整実施記録（場所別）',
    pdfFileName: 'takenakadoboku_nisshi_basho',
    path: '/reports/takenakadoboku_nisshi_basho',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.KtkNippo,
    excelFileName: '作業日報',
    pdfFileName: 'ktk_nippo',
    path: '/reports/ktk_nippo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.UkgNisshi,
    excelFileName: '工事日誌',
    pdfFileName: 'ukg_nisshi',
    path: '/reports/ukg_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.TouyouKy,
    excelFileName: '作業計画届・安全衛生ミーティング日報',
    pdfFileName: 'touyou_ky',
    path: '/reports/touyou_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'field_tree_id',
      'target_date',
      'report_code',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.TouyouNisshi,
    excelFileName: '作業打合せ記録　工事・安全日誌',
    pdfFileName: 'touyou_nisshi',
    path: '/reports/touyou_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.NakanofudouKy,
    excelFileName: '現地・危険予知活動表',
    pdfFileName: 'nakanofudou_ky',
    path: '/reports/nakanofudou_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'field_tree_id',
      'target_date',
      'report_code',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.Cr577KyoritsuRaky,
    excelFileName: 'RAKY',
    path: '/reports/cr577_kyoritsu_raky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr576KyoritsuNisshi,
    excelFileName: '安全日誌',
    pdfFileName: 'kyoritsu_nisshi',
    path: '/reports/cr576_kyoritsu_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr579KonoikeB,
    excelFileName: '安全作業指示書・危険予知活動（B様式）',
    pdfFileName: 'konoike_B',
    path: '/reports/cr579_konoike_b',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'field_tree_id',
      'foreman_user_id',
    ],
  },
  {
    name: ReportNames.Cr578KonoikeA,
    excelFileName: '作業打合せ及び安全巡察点検簿（A様式）',
    pdfFileName: 'konoike_A',
    path: '/reports/cr578_konoike_a',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr136WorkAndDiary2,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'work_and_diary2',
    path: '/reports/cr136_work_and_diary2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'schedule_flag',
      'private_partner_company_flag',
      'is_approved',
      'dayworker_type',
      'dayworker_not_exist_flag',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'manager_user_ids',
      'field_tree_ids',
      'field_group_ids',
      'not_approved_by_me_flag',
      'is_partner_company_approved',
    ],
  },
  {
    name: ReportNames.Cr660KumagaiUchiawase,
    excelFileName: '作業打合せ簿',
    pdfFileName: 'kumagai_uchiawase',
    path: '/reports/cr660_kumagai_uchiawase',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr680CstmGoyoD,
    excelFileName: '安全衛生・品質・環境作業打合せ書（兼）管理日誌（土木）',
    pdfFileName: 'cstm_goyo_d',
    path: '/reports/cr680_cstm_goyo_d',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr681CstmGoyoK,
    excelFileName: '安全衛生・品質・環境作業打合せ書（兼）管理日誌（建築）',
    pdfFileName: 'cstm_goyo_k',
    path: '/reports/cr681_cstm_goyo_k',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr682CivilKy,
    excelFileName: '作業安全指示書及び現地ＫＹ記録表',
    pdfFileName: 'civil_ky',
    path: '/reports/cr682_civil_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'category_code',
      'start_date',
      'end_date',
      'a_item_ids',
      'b_item_ids',
      'c_item_ids',
      'd_item_ids',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr683CivilNisshi,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'civil_nisshi',
    path: '/reports/cr683_civil_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr684ToutetsuNisshi,
    excelFileName: '作業打合せ・安全作業指示・工事・安全衛生日誌【一般用】',
    pdfFileName: 'toutetsu_nisshi',
    path: '/reports/cr684_toutetsu_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr685ToutetsuKy,
    excelFileName: 'ＫＹ活動報告書【一般用】',
    pdfFileName: 'toutetsu_ky',
    path: '/reports/cr685_toutetsu_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr686HonmagumiNisshi,
    excelFileName: '作業安全衛生指示書ー指示書',
    pdfFileName: 'honmagumi_nisshi',
    path: '/reports/cr686_honmagumi_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr687HonmagumiNisshiMeeting,
    excelFileName: '作業安全衛生指示書ーミーティング日報',
    pdfFileName: 'honmagumi_nisshi_meeting',
    path: '/reports/cr687_honmagumi_nisshi_meeting',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr688FkdNisshiK,
    excelFileName: '作業打合せ及び安全・環境日誌（建築系）（その1）',
    pdfFileName: 'fkd_nisshi_k',
    prefixExcelDate: true,
    prefixPdfDate: true,
    path: '/reports/cr688_fkd_nisshi_k',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr689FkdKyK,
    excelFileName: '作業打合せ及び安全・環境日誌（建築系）（その2）',
    pdfFileName: 'fkd_ky_k',
    prefixExcelDate: true,
    prefixPdfDate: true,
    path: '/reports/cr689_fkd_ky_k',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'field_tree_id',
      'foreman_user_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr690TekkenKy,
    excelFileName: '危険予知活動',
    pdfFileName: 'tekken_ky',
    path: '/reports/cr690_tekken_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr692UkgAllWork,
    excelFileName: '作業指示・安全指示書及び現地ＫＹ記録表',
    pdfFileName: 'ukg_all_work',
    path: '/reports/cr692_ukg_all_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'foreman_user_id',
      'field_tree_id',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr693NishimatsuWorkAndDiary,
    excelFileName: '【西松新書式】作業打合せ指示の記録（デジタルフロー用）',
    pdfFileName: 'nishimatsu_work_and_diary',
    path: '/reports/cr693_nishimatsu_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr694TokyuWorkAndDiary,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'tokyu_work_and_diary',
    path: '/reports/cr694_tokyu_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr695TouyouNisshiD,
    excelFileName: '作業打合せ記録　工事・安全日誌（土木）',
    pdfFileName: 'touyou_nisshi_d',
    path: '/reports/cr695_touyou_nisshi_d',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr629AndouhazamaNisshi,
    excelFileName: '安全作業日誌',
    pdfFileName: 'nisshi',
    formatTime: 'HHmm',
    path: '/reports/cr629_andouhazama_nisshi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'field_group_ids',
    ],
  },
  {
    name: ReportNames.Cr630AndouhazamaKyPonchie,
    excelFileName: '危険予知活動表(ポンチ絵枠)',
    pdfFileName: 'ky_ponchie',
    formatTime: 'HHmm',
    path: '/reports/cr630_andouhazama_ky_ponchie',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
      'field_group_ids',
    ],
  },
  {
    name: ReportNames.Cr696DainihondobokuWorkAndDiary,
    excelFileName: '作業安全指示・日誌',
    pdfFileName: 'dainihondoboku_work_and_diary',
    path: '/reports/cr696_dainihondoboku_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr697DainihondobokuAllKyWork,
    excelFileName: '作業安全指示書及び現地ＫＹ記録表',
    pdfFileName: 'dainihondoboku_all_ky_work',
    path: '/reports/cr697_dainihondoboku_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr598GoudakoumutenToukyoAnzenshiji,
    excelFileName: '作業安全指示書・日誌',
    pdfFileName: 'goudakoumuten_toukyo_anzenshiji',
    pdfNoDate: 1,
    path: '/reports/cr598_goudakoumuten_toukyo_anzenshiji',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr599GoudakoumutenToukyoShinchoku,
    excelFileName: '進捗状況報告書',
    pdfFileName: 'goudakoumuten_toukyo_shinchoku',
    pdfNoDate: 1,
    path: '/reports/cr599_goudakoumuten_toukyo_shinchoku',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr800AsanumaWorkAndDiary,
    excelFileName: '【淺沼組】作業安全指示・日誌（作業間連絡調整）',
    pdfFileName: 'asanuma_work_and_diary',
    prefixExcelDateNoUnderscore: true,
    prefixPdfDate: true,
    path: '/reports/cr800_asanuma_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr801KoseikensetsuWorkAndDiary,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'koseikensetsu_work_and_diary',
    path: '/reports/cr801_koseikensetsu_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr802TouakensetsuWorkAndDiary,
    excelFileName: '作業安全指示・日誌',
    pdfFileName: 'touakensetsu_work_and_diary',
    path: '/reports/cr802_touakensetsu_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr803TaiseiSafety,
    excelFileName: 'ペーパレス用【大成建設様式】工事・安全衛生環境打合せ書',
    path: '/reports/cr803_taisei_safety',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr804TaiseiSafety2,
    excelFileName:
      'ペーパレス用【大成建設様式】工事・安全衛生環境打合せ書(場所別)',
    path: '/reports/cr804_taisei_safety2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr805TobishimaKy,
    excelFileName: '作業手順KY',
    pdfFileName: 'KY',
    prefixExcelDate: true,
    prefixPdfDate: true,
    path: '/reports/cr805_tobishima_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr806TokyuTetsudouDobokuWorkAndDiary,
    excelFileName: '作業安全指示・日誌（鉄道土木版）',
    pdfFileName: 'tokyu_tetsudou_doboku_work_and_diary',
    path: '/reports/cr806_tokyu_tetsudou_doboku_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr807KumagaiAllKycWork,
    excelFileName: 'KYC活動記録簿',
    pdfFileName: 'kumagai_all_KYC_work',
    path: '/reports/cr807_kumagai_all_kyc_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.Cr808FudotetraWorkAndDiary,
    excelFileName: '打合せ指示書・安全日誌',
    pdfFileName: 'fudotetra_work_and_diary',
    path: '/reports/cr808_fudotetra_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr809FudotetraAllKyWork,
    excelFileName: 'ＫＹ日報',
    pdfFileName: 'fudotetra_all_ky_work',
    path: '/reports/cr809_fudotetra_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr810TodadouroWorkAndDiary,
    excelFileName: '作業安全指示・日誌',
    pdfFileName: 'todadouro_work_and_diary',
    path: '/reports/cr810_todadouro_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr811TaiseiSafety3,
    excelFileName: '工事・安全衛生環境打合せ書',
    path: '/reports/cr811_taisei_safety3',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
    ],
  },
  {
    name: ReportNames.Cr812TaiseiKy,
    excelFileName: '作業指示・安全指示書及び現地ＫＹ記録表',
    path: '/reports/cr812_taisei_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.Cr813TokyuCnstKy,
    excelFileName: 'KY',
    pdfFileName: 'KY',
    path: '/reports/cr813_tokyu_cnst_ky',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr814TaikiAllKyWork,
    excelFileName: '【大氣社】作業指示・安全指示書及び現地ＫＹ記録表',
    pdfFileName: 'taiki_all_ky_work',
    path: '/reports/cr814_taiki_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'category_code',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr815KgKenchikuWorkAndDiary,
    excelFileName: '打合簿',
    pdfFileName: 'kg_kenchiku_work_and_diary',
    path: '/reports/cr815_kg_kenchiku_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr316MitsuisumitomoNissiBashonashi,
    excelFileName: '作業打合せ及び安全衛生日誌(場所欄無し)',
    pdfFileName: 'mitsuisumitomo_nissi_bashonashi',
    path: '/reports/cr316_mitsuisumitomo_nissi_bashonashi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.CR317SeibuWorkCommonPart3,
    excelFileName: '作業安全指示書（一般）',
    path: '/reports/cr317_seibu_work_common_part3',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr818TekkenWorkAndDiary,
    excelFileName: '作業間連絡調整書',
    pdfFileName: 'tekken_work_and_diary',
    path: '/reports/cr818_tekken_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr819AndouhazamaKyMinisagyoTejyunsho,
    excelFileName: '危険予知活動表(ミニ作業手順書)',
    pdfFileName: 'ky_minisagyo_tejyunsho',
    formatTime: 'HHmm',
    path: '/reports/cr819_andouhazama_ky_minisagyo_tejyunsho',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
      'field_group_ids',
    ],
  },
  {
    name: ReportNames.Cr820AndouhazamaKyUramenNashi,
    excelFileName: '危険予知活動表(裏面無し)',
    pdfFileName: 'ky_uramen_nashi',
    formatTime: 'HHmm',
    path: '/reports/cr820_andouhazama_ky_uramen_nashi',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
      'field_group_ids',
    ],
  },
  {
    name: ReportNames.Cr821AndouhazamaKySetsumeisho,
    excelFileName: '危険予知活動表(説明書)',
    pdfFileName: 'KY_setsumeisho',
    excelNoDate: 1,
    pdfNoDate: 1,
    path: '/reports/cr821_andouhazama_ky_setsumeisho',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr822MorigumiWorkAndDiary,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'morigumi_work_and_diary',
    path: '/reports/cr822_morigumi_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr823KajitaWorkAndDiary,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'kajita_work_and_diary',
    path: '/reports/cr823_kajita_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr824WorkSafeDiaryPs,
    excelFileName: '作業・安全指示確認書兼安全日誌',
    pdfFileName: 'work_safe_diary_ps',
    prefixExcelDate: true,
    prefixPdfDate: true,
    path: '/reports/cr824_work_safe_diary_ps',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr825KyReportPs,
    excelFileName: '危険予知活動実施報告書',
    pdfFileName: 'ky_report_ps',
    prefixExcelDate: true,
    prefixPdfDate: true,
    path: '/reports/cr825_ky_report_ps',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr826Renrakuchouseikiroku,
    excelFileName: '作業間連絡及び調整実施記録（推奨：単日）',
    pdfFileName: 'renrakuchouseikiroku',
    path: '/reports/cr826_renrakuchouseikiroku',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr827KYTetsudo,
    excelFileName: 'KY（鉄道土木版）',
    pdfFileName: 'KY_tetsudo',
    path: '/reports/cr827_ky_tetsudo',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'start_date',
      'end_date',
    ],
  },
  {
    name: ReportNames.Cr828MaruhongumiSagyouUchiawase,
    excelFileName: '作業打合せ書（安全衛生・品質・環境）',
    pdfFileName: 'maruhongumi_sagyou_uchiawase',
    path: '/reports/cr828_maruhongumi_sagyou_uchiawase',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr829SatokogyoWorkAndDiary,
    excelFileName: '作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'satokogyo_work_and_diary',
    path: '/reports/cr829_satokogyo_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr830MeikoWorkAndDiary,
    excelFileName: '【名工建設書式】作業安全指示・日誌（作業間連絡調整書）',
    pdfFileName: 'MEIKO_work_and_diary',
    path: '/reports/cr830_meiko_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
    ],
  },
  {
    name: ReportNames.Cr831MeikoAllKyWork,
    excelFileName: '【名工建設書式】作業安全指示書及びＫＹ記録表',
    pdfFileName: 'MEIKO_All_Ky_Work',
    path: '/reports/cr831_meiko_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'start_date',
      'end_date',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.Cr832KajitaCraneMachineUse,
    excelFileName: '重機機材・作業計画書',
    pdfFileName: 'kajita_crane_machine_use',
    path: '/reports/cr832_kajita_crane_machine_use',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.Cr833KajitaTowercraneUse,
    excelFileName: 'タワークレーン作業計画書安全指示書',
    pdfFileName: 'kajita_towercrane_use',
    path: '/reports/cr833_kajita_towercrane_use',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
      'crane_id',
    ],
  },
  {
    name: ReportNames.Cr834WorkAndDiary,
    excelFileName: '工事打合せ簿兼安全衛生日誌',
    pdfFileName: 'work_and_diary',
    path: '/reports/cr834_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr835AllKyWork,
    excelFileName: '始業ミーティング記録',
    pdfFileName: 'All_Ky_Work',
    path: '/reports/cr835_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
    ],
  },
  {
    name: ReportNames.Cr138SignlessWorkAndDiary,
    excelFileName: 'サインレス版_作業安全指示・日誌',
    pdfFileName: 'signless_work_and_diary',
    path: '/reports/cr138_signless_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
    ],
  },
  {
    name: ReportNames.Cr336SeibuWorkRailPart3,
    excelFileName: '作業安全指示書（鉄道）',
    path: '/reports/cr336_seibu_work_rail_part3',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr837AllKyWork,
    excelFileName: '作業手順ＫＹ日誌',
    pdfFileName: 'All_Ky_Work',
    path: '/reports/cr837_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
    ],
  },
  {
    name: ReportNames.Cr838AndouhazamaAllCraneUse,
    excelFileName: '作業計画書-揚重機',
    pdfFileName: 'All_Crane_Use',
    path: '/reports/cr838_andouhazama_all_crane_use',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
      'crane_id',
      'key_plan_reflect_flag'
    ],
  },
  {
    name: ReportNames.Cr839AndouhazamaMachineryInstruction,
    excelFileName: '作業計画書-機材',
    pdfFileName: 'Machinery_Instruction',
    path: '/reports/cr839_andouhazama_machinery_instruction',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
      'machinery_id',
      'key_plan_reflect_flag',
    ],
  },
  {
    name: ReportNames.Cr840WorkAndDiary,
    excelFileName: '工事安全日誌・作業指示書',
    pdfFileName: 'work_and_diary',
    path: '/reports/cr840_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
  {
    name: ReportNames.Cr841KgDobokuWorkAndDiary2,
    excelFileName: '【熊谷組書式】作業打合簿（土木）',
    pdfFileName: 'kg_doboku_work_and_diary2',
    path: '/reports/cr841_kg_doboku_work_and_diary2',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'foreman_user_id',
      'is_foreman_print',
    ],
  },
  {
    name: ReportNames.Cr842WorkAndDiary,
    excelFileName: '作業指示書',
    pdfFileName: 'work_and_diary',
    path: '/reports/cr842_work_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
    ],
  },
  {
    name: ReportNames.Cr843AllKyWork,
    excelFileName: '危険予知活動表',
    pdfFileName: 'All_Ky_Work',
    path: '/reports/cr843_all_ky_work',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
      'a_item_id',
      'b_item_id',
      'c_item_id',
      'd_item_id',
      'foreman_user_id',
      'is_foreman_print',
      'field_tree_id',
    ],
  },
  {
    name: ReportNames.Cr844SafetyAndDiary,
    excelFileName: '安全環境日誌',
    pdfFileName: 'safety_and_diary',
    path: '/reports/cr844_safety_and_diary',
    args: [
      'file_code',
      'field_id',
      'work_hour_zone',
      'target_date',
      'report_code',
    ],
  },
];

const output = param => {
  return requestWithToken({
    method: 'POST',
    url: path.concat(`/output`),
    data: param,
    responseType: 'blob',
  });
};

const workSchedule = (fieldId, targetDate, workHourZone, params) => {
  return requestWithToken({
    method: 'POST',
    url: path.concat('/output'),
    data: {
      ...params,
      file_code: 2,
      report_code: 2,
      work_hour_zone: workHourZone,
      target_date: targetDate,
      field_id: fieldId,
    },
    responseType: 'blob',
  });
};

const getHeaderReponseURL = pathApi => {
  return requestURL({
    method: 'HEAD',
    url: pathApi,
  });
};

export const PrintService = {
  printReportByReportName,
  makeReportFileName,
  workSchedule,
  output,
  getHeaderReponseURL,
  printReportByReportNameForList,
  handleShowErrorDialogForList,
  getErrorMessageForList,
};
export default PrintService;



// WEBPACK FOOTER //
// ./src/services/Prints.js